/**
 * @module app.cm
 */

import { autoinject, Disposable, ICollectionObserverSplice, BindingEngine } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import * as Mcg from 'app/ext/mcg-informed/interfaces';
import { PlanOfCare } from 'app/ext/mcg-informed/plan-of-care';
import { apiCall } from 'app/shared/lib/api/api';
import { McgInformedService } from 'app/ext/mcg-informed/services/mcg-informed-service';
import { IApiActionResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import * as CM from 'app/cm/models/essette-api';
import { bindable } from 'aurelia-typed-observable-plugin';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { CarePlanGoalWithLocalState } from 'app/cm/models/local-state-extensions';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { EventAggregator } from 'aurelia-event-aggregator';
import { McgInformedDataMethods as mcgMethods } from 'app/ext/mcg-informed/data-methods';
import { focusFirst } from 'app/shared/lib/focus';
import { guid } from 'app/shared/lib/guid';
import { EssAccordionCustomAttribute } from 'app/shared/attributes/ess-collapse-attributes';
import { Toaster } from 'app/shared/lib/toaster';
import { getReference } from 'app/shared/models/essette-types';
import { I18N } from 'aurelia-i18n';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { ITasksConfig, IQueuesConfig } from 'app/core/models/essette-interfaces';
import { ApiDate } from 'app/shared/models/essette-api';

export type HasInterventionsFlag = { hasInterventions: boolean };
export type GoalWithLocalStateAndInterventionFlag = CarePlanGoalWithLocalState & HasInterventionsFlag;

@autoinject()
export class AddMcgGoals extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull
    public caseId: string | null = null;
    protected selectedMcgGuideline: Mcg.Guideline | null = null;
    protected mcgGuideline: Mcg.Assessment | null = null;
    protected mcgGuidelines: Mcg.Guideline[] = [];
    protected priorityLookups: CM.CarePlanGoalPriority[] = [];
    /** Permission that controls whether the MCG Informed integration is available */
    protected hasMcgInformed: boolean = false;
    protected currentGoals: CarePlanGoalWithLocalState[] = [];
    protected allowDuplicateGoals: boolean = false;
    protected selectedGoalsSubscription: Disposable | null = null;
    protected selectedGoals: CM.CarePlanGoalDefinition[] = [];
    protected insertGoals: GoalWithLocalStateAndInterventionFlag[] = [];
    protected selectedMcgProblem: Mcg.Problem | null = null;
    protected selectedMcgGoals: Mcg.IGoal[] | null = null;
    protected selectedMcgInterventions: Mcg.IIntervention[] | null = null;
    protected insertInterventions: CM.CarePlanIntervention[] = [];
    protected customGoal: CM.CarePlanGoalDefinition | undefined = undefined;
    protected id: guid = guid();
    protected problemId: string | null = null;
    protected nextButtonEnabled: boolean = false;
    protected updateAccordionHeader: boolean = false;
    protected selectedGoalsEnabled: boolean = false;

    protected taskConfig: ITasksConfig = {
        serviceRootUrl: 'cm/careplans',
        targetType: 'CM.CarePlanGoal',
        addTargetProperty: this.addCaseProperty.bind(this),
        primaryTargetType: 'CM.CarePlanIntervention',
        labelKey: 'cm:carePlanIntervention.$',
        allowDuplicates: false
    };

    protected queuesConfig: IQueuesConfig = {
        serviceRootUrl: 'cm/tasks',
        targetType: 'CM.Intervention'
    };

    ////****************** Element References ***************************************////
    protected $planOfCare!: PlanOfCare;
    protected $collapsible!: EssAccordionCustomAttribute;
    protected $selectedGoalsli!: HTMLLIElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly carePlanService: CarePlanService,
        private readonly mcgService: McgInformedService,
        private readonly bindingEngine: BindingEngine,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        private readonly context: CasePageContext,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.caseId) { throw new Error('Bindable property [case-id] was not set'); }

        this.checkPermissions();
        this.loadCurrentGoals();
        this.loadLookups();
    }

    protected attached(): void {
        this.observeSelectedTasks();

        this.events.subscribe('mcg-goal-checked-changed', (value: boolean) => {
            this.nextButtonEnabled = value;
        });
    }

    protected detached(): void {
        if(this.selectedGoalsSubscription) {
            this.selectedGoalsSubscription.dispose();
        }
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public submittedChanges?: any;

    public async submit(): Promise<boolean> {
        let success: boolean = true;
        if(this.selectedMcgProblem && !this.problemExistsOnCareplan(this.selectedMcgProblem)) success = await this.saveSelectedProblem();
        success = success && await this.saveSelectedGoals();
        return success;
    }

    public reset(): void {
        this.selectedMcgProblem = null;
        this.selectedGoals.splice(0);
        this.selectedMcgGoals?.splice(0);
        this.insertGoals.splice(0);
        this.selectedMcgInterventions?.splice(0);
        this.insertInterventions.splice(0);
        this.selectedMcgGuideline = null;
        this.mcgGuideline = null;
        this.updateAccordionHeader = false;
        this.selectedGoalsEnabled = false;

        this.problemId = null;

        if(this.$planOfCare) this.$planOfCare.reset();
        this.$collapsible.close(1);
        this.$collapsible.open(0);
    }

    private delayedFocus() {
        setTimeout(() => focusFirst(document.getElementsByTagName("add-mcg-goals")[0]), 250);
    }

    ////****************** Functions ************************************************////
    protected addCaseProperty(model: CM.CaseTask): void {
        model.case = { id: this.caseId };
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.allowDuplicateGoals = !!getApiSetting(this.context.settings, 'carePlan.goals.$allowDuplicates');
        this.hasMcgInformed = this.env.config.integrations.includes('cci') && this.accountContext.hasRouteAccess('cci.assessment');
    }

    protected mcgGoalsOpened(): void {
        this.selectedGoalsEnabled = false;
    }

    protected observeSelectedTasks(): void {
        // observer only works on a modified array. if the array is replaced, the observer will not function for it any longer unless subscribed again
        if(this.selectedGoalsSubscription) {
            this.selectedGoalsSubscription.dispose();
        }
        this.selectedGoalsSubscription = this.bindingEngine
            .collectionObserver(
                this.selectedGoals) // observe the selectedTasks array, which changes in the multi-select Task Definition Select element
            .subscribe(
                this.selectedGoalsChanged
                    .bind(this)); // pass the splice information (addedCount, index, removed) to selectedTasksChanged method

        // subscription disposed of in detached()
    }

    protected async selectedMcgGuidelineChanged(): Promise<void> {
        this.updateAccordionHeader = false;
        this.nextButtonEnabled = false;
        this.insertGoals.splice(0);

        if(!this.selectedMcgGuideline) {
            this.mcgGuideline = null;
            return;
        }
        const hsim = this.selectedMcgGuideline.Hsim;
        const edition = this.selectedMcgGuideline.Edition;
        await this.loadSelectedMcgGuideline(hsim, edition);
        this.$planOfCare.reload();
    }

    public async next(): Promise<void> {
        this.selectedGoalsEnabled = true;
        this.insertInterventions.splice(0);
        this.insertGoals.splice(0);
        this.selectedMcgProblem = await this.$planOfCare.getSelectedProblem();
        this.selectedMcgGoals = await this.$planOfCare.getSelectedGoals();
        this.selectedMcgInterventions = await this.$planOfCare.getSelectedInterventions();


        if(this.selectedMcgGoals.length > 0) {
            this.selectedMcgGoals.forEach(goal => {
                const g: GoalWithLocalStateAndInterventionFlag = {
                    isCustom: true,
                    description: mcgMethods.sanitizeText(goal),
                    definition: { id: this.customGoal?.id },
                    case: { id: this.caseId },
                    _state: { isSaved: false, addToLibrary: false },
                    externalSystem: { code: 'CCI' },
                    externalIdentifier: goal.uid,
                    externalAssessmentIdentifier: this.selectedMcgGuideline?.hsim,
                    externalAssessmentVersion: this.selectedMcgGuideline?.edition,
                    hasInterventions: this.selectedMcgInterventions?.some(i => i.uid.substring(0, i.uid.lastIndexOf('-')) === goal.uid) || false
                };
                this.insertGoals.push(g);
            });
        }

        if(this.selectedMcgInterventions.length > 0) {
            for(const intervention of this.selectedMcgInterventions) {
                if(this.$planOfCare.interventionExistsOnCareplan(intervention)) continue;

                const i: CM.CarePlanIntervention = {
                    isCustom: true,
                    description: mcgMethods.sanitizeText(intervention),
                    delegated: { queue: null },
                    externalSystem: { code: 'CCI' },
                    externalIdentifier: intervention.uid,
                    summary: {
                        mediaType: { code: 'mcg.annotation' },
                        taskSummary: intervention.text
                    }
                };
                this.insertInterventions.push(i);
            }
        }
        this.$selectedGoalsli.classList.remove('disabled');
        this.$collapsible.close(0);
        this.$collapsible.open(1);
        this.updateAccordionHeader = true;
        this.delayedFocus();
    }

    /** Called when a user removes a goal from the grid. Will ultimately trigger the selectedGoalsChanged() method */
    protected removeGoal(goal: GoalWithLocalStateAndInterventionFlag): void {
        if(goal.isCustom) {
            // Custom tasks are not entries in selectedTasks, so no need to find and remove it
            const index = this.insertGoals.indexOf(goal);
            if(index >= 0) {
                this.insertGoals.splice(index, 1);
            }
        }
        else {
            // Find the task in the selectedTasks array and remove it. This will trigger the selectedTasksChanged(), which will update insertTasks
            const st = this.selectedGoals.find(t => t.id === (goal.definition && goal.definition.id));
            if(st) {
                this.selectedGoals.splice(this.selectedGoals.indexOf(st), 1);
            }
        }
    }

    protected goalExistsOnCareplan(goal: CarePlanGoalWithLocalState): boolean {
        for(const cg of this.currentGoals) {
            if(cg.externalIdentifier === goal.externalIdentifier) return true;
        }

        return false;
    }

    protected problemExistsOnCareplan(problem: Mcg.Problem): boolean {
        let exists: boolean = false;

        for(const cg of this.currentGoals) {
            if(cg.problem?.externalIdentifier === problem.uid) {
                exists = true;
                if(cg.problem.id) this.problemId = cg.problem.id;
                break;
            }
        }

        return exists;
    }

    protected getSelectedInterventions(externalGoalIdentifier: string): CM.CarePlanIntervention[] {
        return this.insertInterventions.filter(i => {
            const goalInterventions = this.selectedMcgGoals?.find(g => g.uid == externalGoalIdentifier)?.interventionsSet;
            return goalInterventions?.find(gi => gi.uid == i.externalIdentifier);
        });
    }

    protected copyPriority(priority: CM.CarePlanGoalPriority | null): void {
        this.insertGoals.forEach(g => {
            g.priority = priority;
        });
    }

    protected copyTargetDate(date: ApiDate | null): void {
        this.insertGoals.forEach(g => {
            g.targetDate = date;
        });
    }

    ////****************** Events ***************************************************////
    protected selectedGoalsChanged(splices: ICollectionObserverSplice<CM.CarePlanGoalDefinition>[]): void {
        if(this.insertGoals.length === 0 && this.selectedGoals.length === 0) return;

        for(var i = 0; i < splices.length; i++) {
            var splice = splices[i];

            var goalsAdded = this.selectedGoals.slice(splice.index, splice.index + splice.addedCount);
            if(goalsAdded.length > 0) {

                goalsAdded.forEach(
                    goal => {
                        const g: GoalWithLocalStateAndInterventionFlag = {
                            isCustom: false,
                            description: goal.description,
                            definition: { id: goal.id },
                            case: { id: this.caseId },
                            _state: { isSaved: false, addToLibrary: false },
                            hasInterventions: false
                        };
                        this.insertGoals.push(g);
                    });
            }

            if(splice.removed.length) {
                (splice.removed as CM.CarePlanGoalDefinition[]).forEach(
                    goal => {
                        let i = this.insertGoals.find(g => (g.definition && g.definition.id) === goal.id);
                        if(i) {
                            this.insertGoals.splice(this.insertGoals.indexOf(i), 1);
                        }
                    });
            }
        }
    }

    /** Takes a goal from the insertGoals grid and constructs a new copy to be sent to the API */
    protected buildSaveGoal(goal: CarePlanGoalWithLocalState): CarePlanGoalWithLocalState {
        if(!goal._state) { goal._state = {}; }

        let saveGoal: CarePlanGoalWithLocalState = {
            case: { id: this.caseId },
            isCustom: goal.isCustom,
            priority: getReference(goal.priority) || undefined,
            targetDate: goal.targetDate,
            definition: (!goal.isCustom && getReference(goal.definition)) || undefined,
            description: (!goal.definition && goal.description) || (goal.externalSystem && goal.description) || undefined,
            externalSystem: goal.externalSystem,
            externalIdentifier: goal.externalIdentifier
        };

        if(goal.externalSystem?.code === 'CCI' && this.selectedMcgGuideline) {
            saveGoal.externalAssessmentIdentifier = this.selectedMcgGuideline.Hsim;
            saveGoal.externalAssessmentVersion = this.selectedMcgGuideline.Edition;
        }

        return saveGoal;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadLookups(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const fields = ['description'];

        try {
            this.state.beginLoad();

            const response = await this.carePlanService.loadCarePlanGoalPriorities(options, undefined, fields);
            this.priorityLookups = response.results;

            if(!this.hasMcgInformed) return;

            let mcgGuidelinesResponse = await this.mcgService.loadGuidelineList();
            if(mcgGuidelinesResponse.isSuccessful) {
                this.mcgGuidelines = mcgGuidelinesResponse.result.guidelines;
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
            // expand Goals section when data is done loading
            this.$collapsible.open(0);
        }
    }

    @apiCall
    private async loadSelectedMcgGuideline(hsim: string, edition: string): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.mcgService.getGuidelineContent(hsim, edition);
            this.mcgGuideline = response.result;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async loadCurrentGoals(): Promise<void> {
        if(!this.caseId) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            includeInactive: false
        };

        const includes = [
            'definition',
            'externalSystem',
            'problem'
        ];

        const fields = [
            'definition.id',
            'externalSystem.code',
            'problem.id'
        ];

        try {
            this.state.beginLoad();
            const response = await this.carePlanService.loadCaseCarePlan(this.caseId, options, includes, fields);
            this.currentGoals = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveSelectedProblem(): Promise<boolean> {
        const problem: CM.CarePlanProblem = {
            description: this.selectedMcgProblem?.text,
            isCustom: true,
            case: { id: this.caseId },
            externalSystem: { code: 'CCI' },
            externalIdentifier: this.selectedMcgProblem?.uid
        };

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const response = await this.carePlanService.insertCarePlanProblem(problem);
            isSuccessful = response.isSuccessful;
            this.problemId = response.id;
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('cm:carePlanProblem.$', isSuccessful);
            this.state.endSave();
            return isSuccessful;
        }
    }

    /** Iterates through the goals and saves each one. */
    @apiCall
    private async saveSelectedGoals(): Promise<boolean> {
        const duplicateGoals: string[] = [];

        const goals = this.insertGoals
            .filter(g => {
                // If user clicks save a second time, we skip goals that might have been saved already.
                return !(g._state && g._state.isSaved);
            })
            .map(g => {
                if(this.allowDuplicateGoals && this.currentGoals.some(cg => (cg.definition && cg.definition.id) === (g.definition && g.definition.id) && !g.externalSystem)) {
                    duplicateGoals.push(g.description || '');
                }

                const goal = this.buildSaveGoal(g);

                return goal;
            });

        /** Show confirmation to add duplicate goals */
        if(duplicateGoals.length > 0) {
            const message: string = this.i18n.tr('ui:messages.duplicate.confirm', { count: duplicateGoals.length, what: '$t(cm:carePlanGoal.$)' });
            const result = await confirmation.showCustom(message, duplicateGoals.join('\n'), confirmation.options.StandardConfirm);
            if(result.dialogResponse === confirmation.options.CANCEL) {
                //canceling means we just stop
                return Promise.resolve(false);
            }
        }

        try {
            this.state.beginSave();

            const promises = new Array<Promise<IApiActionResults>>();
            let mcgGoalsAreSuccessful = true;

            for(const g of goals) {
                if(!g.externalSystem) promises.push(this.carePlanService.insertCarePlanGoal(g));
                else {
                    let goalId: string | null = null;

                    if(!this.goalExistsOnCareplan(g)) {
                        if(this.problemId) g.problem = { id: this.problemId };
                        let mcgResponse = await this.carePlanService.insertCarePlanGoal(g);
                        if(mcgResponse.isSuccessful && mcgResponse.id) goalId = mcgResponse.id;
                    }
                    else {
                        for(const cg of this.currentGoals) {
                            if(cg.externalIdentifier === g.externalIdentifier && cg.id) {
                                goalId = cg.id;
                                break;
                            }
                        }
                    }

                    if(goalId && this.insertInterventions.length > 0) await this.saveSelectedInterventions(goalId, g.externalIdentifier!);
                    if(!goalId) mcgGoalsAreSuccessful = false;
                }
            }

            const responses = await Promise.all(promises);
            if(responses.every(r => (r.isSuccessful)) && mcgGoalsAreSuccessful) {
                return Promise.resolve(true);
            }

            return Promise.resolve(false);
        } catch(error) {
            this.toaster.standard.saved('cm:carePlanGoal.$', false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async saveSelectedInterventions(goalId: string, externalGoalIdentifier: string): Promise<Boolean> {
        const mcgGoal: Mcg.IGoal | undefined = this.selectedMcgGoals?.find(g => g.uid === externalGoalIdentifier);
        if(!mcgGoal) return Promise.resolve(false);

        const interventions: CM.CarePlanIntervention[] = [];
        this.insertInterventions.filter(i => mcgGoal?.interventionsSet.some(gi => gi.uid === i.externalIdentifier)).forEach(i => {
            let intervention: CM.CarePlanIntervention = {
                goal: { id: goalId },
                isCustom: i.isCustom,
                description: i.description,
                due: i.due,
                externalSystem: i.externalSystem,
                externalIdentifier: i.externalIdentifier,
                summary: {
                    mediaType: { code: 'mcg.annotation' },
                    taskSummary: i.summary?.taskSummary
                }
            };

            if(i.delegated && i.delegated.queue) {
                intervention.delegated = {
                    queue: getReference(i.delegated.queue),
                    to: i.delegated.to && i.delegated.queue?.delegated ? {
                        id: i.delegated.to.id,
                        type: i.delegated.queue.delegated.type
                    } : undefined
                };
            }

            interventions.push(intervention);
        });

        try {
            this.state.beginSave();

            const promises = new Array<Promise<IApiActionResults>>();
            interventions.forEach(async i => {
                promises.push(this.carePlanService.insertCarePlanIntervention(i));
            });

            const responses = await Promise.all(promises);
            if(responses.every(r => r.isSuccessful)) {
                this.toaster.standard.saved('cm:carePlanIntervention.$', true);
                return Promise.resolve(true);
            }

            return Promise.resolve(false);
        } catch(error) {
            this.toaster.standard.saved('cm:carePlanIntervention.$', false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

}
